import { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, FormHelperText, Tooltip } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import { getDepartureIndexForValue, isFeaturePostFire } from '../../../utils/FeatureHelper';
import { useSelector } from 'react-redux';
import {
    getByClassEntityTypeJoinFeature,
    getByLevel, getFilteredCategoryAssociations,
} from '../../../utils/CategoryAssociationHelper';

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
});

const theme = createTheme({
    overrides: {},
});

export default function DetailsSelect({
    detail,
    field,
    onFieldChange,
    fieldInfo,
    isRequired,
    allFields,
    fieldIsStatic,
    classValue,
    setClassValue,
    categoryValue,
    setCategoryValue,
    unitOfMeasureValue,
    setUnitOfMeasureValue,
    getAllApprovalFieldsCount,
    fundingSourceOptions,
    departureOptions,
    statusOptions,
    typeValue,
    setTypeValue,
    subTypeValue,
    setSubTypeValue,
    agencyValue,
    locationDepartmentValue,
    setLocationDepartmentValue,
    programValue,
    setProgramValue,
    valueReference,
}) {
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const actualsTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualsActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });

    // const postFireValueReference = useSelector((state) => state.details.postFireValueReference);

    const isActual = useSelector((state) => state.details.isActual);
    const isTreatment = useSelector((state) => state.details.isTreatment);

    const classes = useStyles();
    const notSavedClasses = useNotSavedStyles();

    const [selectValue, setSelectValue] = useState('');
    const [initialLoad, setInitialLoad] = useState(true);
    const [tooltipText, setTooltipText] = useState('');
    const [fieldIsDisabled, setFieldIsDisabled] = useState(false);

    const isPostFire = isFeaturePostFire(detail?.attributes);

    useEffect(() => {
        const inputValueMissingInside = () => {
            if (detail && detail.attributes) {
                if (!fieldIsStatic && !detail.attributes[field]) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        };
        if (detail && detail.attributes) {
            if (detail.attributes[field]) {
                if (field === allFields.fundingSource) {
                    if (detail.attributes[field] !== null && detail.attributes[field] !== '') {
                        let fundingValue = detail.attributes[field];
                        fundingSourceOptions.forEach((option, i) => {
                            if (
                                option[FieldNames.fundingSources.fundingSource] &&
                                option[FieldNames.fundingSources.fundingSource].includes(
                                    detail.attributes[field]
                                )
                            ) {
                                fundingValue = option[FieldNames.fundingSources.fundingSource];
                            }
                        });
                        setSelectValue(fundingValue);
                        inputValueMissingInside();
                    }
                } else if (field === allFields.vegDeparturePercentageManual) {
                    const depIndexVal = getDepartureIndexForValue(detail.attributes[field]);
                    setSelectValue(depIndexVal);
                } else if (field === allFields.estimatedSuccessProbability) {
                    const finalValue = detail.attributes[field].replace(/'/g, '');
                    setSelectValue(finalValue);
                } else if (field === allFields.unitOfMeasure && isPostFire) {
                    if (
                        detail?.attributes?.[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.line
                    ) {
                        setFieldIsDisabled(true);
                    } else if (
                        detail?.attributes?.[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.polygon
                    ) {
                        setFieldIsDisabled(true);
                    } else {
                        setFieldIsDisabled(false);
                    }
                    setSelectValue(detail.attributes[field]);
                } else {
                    if (detail.attributes[field] !== null && detail.attributes[field] !== '') {
                        setSelectValue(detail.attributes[field]);
                        inputValueMissingInside();
                    }
                }
            } else {
                if (field === allFields.isRTRL) {
                    if (initialLoad) {
                        if (detail && detail.attributes) {
                            const isBIA =
                                detail.attributes[FieldNames.featureFields.fundingAgency] &&
                                detail.attributes[FieldNames.featureFields.fundingAgency] ===
                                    FieldNames.agencies.bia
                                    ? true
                                    : false;
                            if (isBIA) {
                                setSelectValue(FieldNames.yesNo.no);
                                onFieldChange(field, FieldNames.yesNo.no);
                                inputValueMissingInside();
                            } else {
                                setSelectValue('');
                            }
                        } else {
                            setSelectValue('');
                        }
                    }
                } else if (field === allFields.unitOfMeasure && isPostFire) {
                    if (
                        detail?.attributes?.[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.line
                    ) {
                        setSelectValue(FieldNames.unitOfMeasureValues.feetMiles);
                        onFieldChange(field, FieldNames.unitOfMeasureValues.feetMiles);
                        setFieldIsDisabled(true);
                    } else if (
                        detail?.attributes?.[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.polygon
                    ) {
                        setSelectValue(FieldNames.unitOfMeasureValues.acres);
                        onFieldChange(field, FieldNames.unitOfMeasureValues.acres);
                        setFieldIsDisabled(true);
                    } else {
                        setSelectValue('');
                        setFieldIsDisabled(false);
                    }
                } else if (field === allFields.isInteragency) {
                    if (initialLoad) {
                        if (detail && detail.attributes) {
                            if (!isPostFire) {
                                setSelectValue(FieldNames.yesNo.no);
                                onFieldChange(field, FieldNames.yesNo.no);
                                inputValueMissingInside();
                            } else {
                                setSelectValue('');
                            }
                        } else {
                            setSelectValue('');
                        }
                    }
                } else {
                    setSelectValue('');
                    setFieldIsDisabled(false);
                }
            }
            setInitialLoad(false);
        }
    }, [
        detail,
        field,
        fieldInfo,
        onFieldChange,
        initialLoad,
        allFields,
        setClassValue,
        fieldIsStatic,
        fundingSourceOptions,
    ]);

    const getPostFireMenuOptions = (detailAttributes, categoryAssociationField, valueReference) => {
        const menuOptions = [];
        menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
        const values = getByLevel(
            detailAttributes,
            categoryAssociationField,
            valueReference
        );
        const sortedOptionsArray = values.sort((a, b) => (a < b ? -1 : 1));

        sortedOptionsArray?.forEach((option, i) => {
            menuOptions.push(
                <MenuItem value={option} key={i + 1}>
                    {option}
                </MenuItem>
            );
        });

        return menuOptions;
    }

    const getItemsForDomain = (fieldInfo) => {
        const fieldOptions = fieldInfo.domain;

        //Check in here to see if this is Class or Category
        //Filter Category and Type based off of the previous settings.  Class controls Category, Category controls Type
        if (
            allFields.estimatedSuccessProbability &&
            fieldInfo.name === allFields.estimatedSuccessProbability
        ) {
            if (fieldOptions && fieldOptions.codedValues) {
                const arrayForSort = [...fieldOptions.codedValues];
                arrayForSort?.sort(function (a, b) {
                    return parseInt(a.name) - parseInt(b.name);
                });
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                arrayForSort?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option.code} key={i + 1}>
                            {option.name}%
                        </MenuItem>
                    );
                });
                return menuOptions;
            } else {
                return <div></div>;
            }
        } else {
            if (fieldInfo.name === allFields.status) {
                let menuOptions = [];
                statusOptions?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option} disabled key={i + 1}>
                            {option}
                        </MenuItem>
                    );
                });
                return menuOptions;
            } else if (fieldInfo.name === allFields.program) {
                const limitedCategoryTypes = getByClassEntityTypeJoinFeature(
                    detail?.attributes,
                    FieldNames.categoryAssociationFields.programs
                );
                //We see all if Activity or Treatment is selected based on whether we are looking at an activity or treatment
                let possibleOptions = [];

                limitedCategoryTypes?.forEach((catType) => {
                    if (!possibleOptions.includes(catType)) {
                        possibleOptions.push(catType);
                    }
                });

                const menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);

                let optionsArr = [];
                optionsArr.push({ code: selectValue, name: selectValue });

                limitedCategoryTypes?.forEach((option, i) => {
                    if (possibleOptions && possibleOptions.includes(option)) {
                        if (
                            optionsArr.filter(
                                (existingOption) =>
                                    existingOption.code === option && existingOption.name === option
                            ).length === 0
                        ) {
                            optionsArr.push({ code: option, name: option });
                        }
                    }
                });

                const sortedOptionsArray = optionsArr.sort((a, b) => (a.name < b.name ? -1 : 1));

                sortedOptionsArray?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option.code} key={i + 1}>
                            {option.name}
                        </MenuItem>
                    );
                });

                return menuOptions;
            } else if (fieldInfo.name === allFields.category) {
                if ((programValue && isPostFire) || !isPostFire) {
                    let limitedCategoryTypes = getByLevel(
                        detail?.attributes,
                        FieldNames.categoryAssociationFields.categories,
                        valueReference
                    );
                    const menuOptions = [];
                    if (programValue && isPostFire){
                        return getPostFireMenuOptions(detail?.attributes, FieldNames.categoryAssociationFields.categories, valueReference);
                    }

                    // console.log(postFireValueReference)
                    //We see all if Activity or Treatment is selected based on whether we are looking at an activity or treatment
                    let possibleOptions = [];

                    limitedCategoryTypes?.forEach((catType) => {
                        if (!possibleOptions.includes(catType)) {
                            possibleOptions.push(catType);
                        }
                    });


                    menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);

                    let optionsArr = [];
                    optionsArr.push({ code: selectValue, name: selectValue });

                    if (
                        detail &&
                        detail.attributes &&
                        detail.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
                    ) {
                        const allowedCategories = [
                            FieldNames.categoriesForChecking.plantSourceDevelopment,
                            FieldNames.categoriesForChecking.communications,
                            FieldNames.categoriesForChecking.multiYear,
                            FieldNames.categoriesForChecking.programManagement,
                        ];
                        if (!isPostFire && !isTreatment) {
                            //Fuels Activities
                            limitedCategoryTypes?.forEach((option, i) => {
                                if (
                                    possibleOptions &&
                                    possibleOptions.includes(option) &&
                                    allowedCategories.includes(option)
                                ) {
                                    if (
                                        optionsArr.filter(
                                            (existingOption) =>
                                                existingOption.code === option &&
                                                existingOption.name === option
                                        ).length === 0
                                    ) {
                                        optionsArr.push({ code: option, name: option });
                                    }
                                }
                            });
                        } else {
                            //All Post Fire points
                            limitedCategoryTypes?.forEach((option, i) => {
                                if (
                                    possibleOptions &&
                                    possibleOptions.includes(option) &&
                                    option !== FieldNames.categoriesForChecking.programManagement
                                ) {
                                    if (
                                        optionsArr.filter(
                                            (existingOption) =>
                                                existingOption.code === option &&
                                                existingOption.name === option
                                        ).length === 0
                                    ) {
                                        optionsArr.push({ code: option, name: option });
                                    }
                                }
                            });
                        }
                    } else {
                        limitedCategoryTypes?.forEach((option, i) => {
                            if (
                                possibleOptions &&
                                possibleOptions.includes(option) &&
                                option !== FieldNames.categoriesForChecking.programManagement
                            ) {
                                if (
                                    optionsArr.filter(
                                        (existingOption) =>
                                            existingOption.code === option &&
                                            existingOption.name === option
                                    ).length === 0
                                ) {
                                    optionsArr.push({ code: option, name: option });
                                }
                            }
                        });
                    }

                    const sortedOptionsArray = optionsArr.sort((a, b) =>
                        a.name < b.name ? -1 : 1
                    );

                    sortedOptionsArray?.forEach((option, i) => {
                        menuOptions.push(
                            <MenuItem value={option.code} key={i + 1}>
                                {option.name}
                            </MenuItem>
                        );
                    });

                    return menuOptions;
                } else {
                    //if Program is null then no types can be selected
                    return;
                }
            } else if (fieldInfo.name === allFields.type) {
                const menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                if(programValue && isPostFire ) {
                     return getPostFireMenuOptions(
                         detail?.attributes,
                         FieldNames.categoryAssociationFields.types,
                         valueReference
                     );
                } else {
                    if (categoryValue) {
                           let limitedCategoryTypes = getByLevel(
                               detail?.attributes,
                               FieldNames.categoryAssociationFields.types,
                               valueReference
                           );
                        //Base menu items on categorySelected
                        let possibleOptions = [];

                        limitedCategoryTypes?.forEach((catType) => {
                            if (!possibleOptions.includes(catType)) {
                                possibleOptions.push(catType);
                            }
                        });



                        let optionsArr = [];
                        optionsArr.push({ code: selectValue, name: selectValue });

                        if (
                            detail &&
                            detail.attributes &&
                            detail.attributes[FieldNames.featureFields.isPoint] ===
                                FieldNames.yesNo.yes
                        ) {
                            if (!isPostFire && !isTreatment) {
                                //Fuels Activites only
                                const allowedTypes = [
                                    FieldNames.typesForChecking.nurseryStock,
                                    FieldNames.typesForChecking.mediaPublicInfo,
                                    FieldNames.typesForChecking.commOutreach,
                                    FieldNames.typesForChecking.other,
                                    FieldNames.typesForChecking.wildlandFire,
                                    FieldNames.typesForChecking.agreement,
                                    FieldNames.typesForChecking.contract,
                                    FieldNames.typesForChecking.tribalIndirect,
                                ];
                                const isBia =
                                    agencyValue?.includes(FieldNames.agencies.bia) ||
                                    (detail.attributes[FieldNames.featureFields.fundingAgency] &&
                                        detail.attributes[
                                            FieldNames.featureFields.fundingAgency
                                        ].includes(FieldNames.agencies.bia))
                                        ? true
                                        : false;
                                limitedCategoryTypes?.forEach((option, i) => {
                                    if (
                                        possibleOptions &&
                                        possibleOptions.includes(option) &&
                                        (!isBia || (allowedTypes.includes(option) && isBia))
                                    ) {
                                        if (
                                            optionsArr.filter(
                                                (existingOption) =>
                                                    existingOption.code === option &&
                                                    existingOption.name === option
                                            ).length === 0
                                        ) {
                                            optionsArr.push({ code: option, name: option });
                                        }
                                    }
                                });
                            } else {
                                limitedCategoryTypes?.forEach((option, i) => {
                                    if (possibleOptions?.includes(option)) {
                                        if (
                                            optionsArr.filter(
                                                (existingOption) =>
                                                    existingOption.code === option &&
                                                    existingOption.name === option
                                            ).length === 0
                                        ) {
                                            optionsArr.push({ code: option, name: option });
                                        }
                                    }
                                });
                            }
                        } else {
                            limitedCategoryTypes?.forEach((option, i) => {
                                if (possibleOptions && possibleOptions.includes(option)) {
                                    if (
                                        optionsArr.filter(
                                            (existingOption) =>
                                                existingOption.code === option &&
                                                existingOption.name === option
                                        ).length === 0
                                    ) {
                                        optionsArr.push({ code: option, name: option });
                                    }
                                }
                            });
                        }

                        const sortedOptionsArray = optionsArr.sort((a, b) =>
                            a.name < b.name ? -1 : 1
                        );

                        sortedOptionsArray?.forEach((option, i) => {
                            menuOptions.push(
                                <MenuItem value={option.code} key={i + 1}>
                                    {option.name}
                                </MenuItem>
                            );
                        });

                        return menuOptions;
                    } else {
                        //If Category is null then no types can be selected
                        return;
                    }
                }

            } else if (fieldInfo.name === allFields.subType) {
                if(programValue && isPostFire) {
                    return getPostFireMenuOptions(
                        detail?.attributes,
                        FieldNames.categoryAssociationFields.subTypes,
                        valueReference
                    );
                } else  if (typeValue) {
                    //Base menu items on categorySelected
                    let possibleOptions = [];
                    let limitedCategoryTypes = getByLevel(
                        detail?.attributes,
                        FieldNames.categoryAssociationFields.subTypes,
                        valueReference
                    );
                    limitedCategoryTypes?.forEach((option) => {
                        if (option === FieldNames.subTypesForChecking.dna) {
                            if (isPostFire || agencyValue?.includes(FieldNames.agencies.blm)) {
                                possibleOptions.push(option);
                            }
                        } else {
                            possibleOptions.push(option);
                        }
                    });

                    const menuOptions = [];
                    menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);

                    let optionsArr = [];
                    optionsArr.push({ code: selectValue, name: selectValue });

                    limitedCategoryTypes?.forEach((option, i) => {
                        if (possibleOptions && possibleOptions.includes(option)) {
                            if (
                                optionsArr.filter(
                                    (existingOption) =>
                                        existingOption.code === option &&
                                        existingOption.name === option
                                ).length === 0
                            ) {
                                optionsArr.push({ code: option, name: option });
                            }
                        }
                    });

                    const sortedOptionsArray = optionsArr.sort((a, b) =>
                        a.name < b.name ? -1 : 1
                    );

                    sortedOptionsArray?.forEach((option, i) => {
                        menuOptions.push(
                            <MenuItem value={option.code} key={i + 1}>
                                {option.name}
                            </MenuItem>
                        );
                    });

                    return menuOptions;
                } else {
                    //If Type is null then no subTypes can be selected
                    return;
                }
            } else if (fieldInfo.name === allFields.unitOfMeasure) {
                if ((typeValue && subTypeValue) || (typeValue && !subTypeValue)) {
                    //Base menu items on categorySelected
                    let possibleOptions = [];
                    let limitedCategoryTypes = getByLevel(
                        detail?.attributes,
                        FieldNames.categoryAssociationFields.measurements
                    );

                    if (subTypeValue) {
                        limitedCategoryTypes?.forEach((option) => {
                            if (subTypeValue) {
                                if (subTypeValue === FieldNames.subTypesForChecking.dna) {
                                    if (
                                        agencyValue &&
                                        agencyValue.includes(FieldNames.agencies.blm)
                                    ) {
                                        possibleOptions.push(option);
                                    }
                                } else {
                                    possibleOptions.push(option);
                                }
                            }
                        });
                    } else {
                        limitedCategoryTypes?.forEach((option) => {
                            if (typeValue && option) {
                                possibleOptions.push(option);
                            }
                        });
                    }

                    const menuOptions = [];
                    menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);

                    let optionsArr = [];
                    optionsArr.push({ code: selectValue, name: selectValue });

                    limitedCategoryTypes?.forEach((option, i) => {
                        if (possibleOptions && possibleOptions.includes(option)) {
                            if (
                                optionsArr.filter(
                                    (existingOption) =>
                                        existingOption.code === option &&
                                        existingOption.name === option
                                ).length === 0
                            ) {
                                if (
                                    isPostFire &&
                                    detail?.attributes?.[FieldNames.featureFields.joinFeature] ===
                                        FieldNames.joinFeatureValues.point
                                ) {
                                    if (
                                        option !== FieldNames.unitOfMeasureValues.feetMiles &&
                                        option !== FieldNames.unitOfMeasureValues.acres
                                    ) {
                                        optionsArr.push({ code: option, name: option });
                                    }
                                } else {
                                    optionsArr.push({ code: option, name: option });
                                }
                            }
                        }
                    });

                    const sortedOptionsArray = optionsArr.sort((a, b) =>
                        a.name < b.name ? -1 : 1
                    );

                    sortedOptionsArray?.forEach((option, i) => {
                        menuOptions.push(
                            <MenuItem value={option.code} key={i + 1}>
                                {option.name}
                            </MenuItem>
                        );
                    });

                    return menuOptions;
                } else {
                    //If Type is null then no subTypes can be selected
                    return;
                }
            } else if (fieldInfo.name === allFields.class) {
                if (classValue) {
                    const menuOptions = [];
                    menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                    menuOptions.push(
                        <MenuItem value={classValue} key='1' style={{ height: 30 }}>
                            {classValue}
                        </MenuItem>
                    );
                    return menuOptions;
                } else {
                    const menuOptions = [];
                    menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                    if (detail && detail.attributes) {
                        let freshClassVal = '';
                        if (isActual) {
                            if (isTreatment) {
                                freshClassVal = FieldNames.classTypes.actualTreatment;
                            } else {
                                freshClassVal = FieldNames.classTypes.actualActivity;
                            }
                        } else {
                            if (isTreatment) {
                                freshClassVal = FieldNames.classTypes.treatment;
                            } else {
                                freshClassVal = FieldNames.classTypes.activity;
                            }
                        }
                        menuOptions.push(
                            <MenuItem value={freshClassVal} key='1' style={{ height: 30 }}>
                                {freshClassVal}
                            </MenuItem>
                        );
                    }
                    return menuOptions;
                }
            } else if (fieldInfo.name === allFields.feasibility) {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                fieldOptions?.codedValues?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option.code} key={i + 1}>
                            {option.name}
                        </MenuItem>
                    );
                });
                return menuOptions;
            } else if (fieldInfo.name === allFields.department) {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='N/A' key='0' style={{ height: 30 }}></MenuItem>);
                let optionsArr = [];
                optionsArr.push({ code: selectValue, name: selectValue });
                if (fieldOptions?.codedValues?.length) {
                    fieldOptions.codedValues.forEach((option, i) => {
                        if (
                            optionsArr.filter(
                                (existingOption) =>
                                    existingOption.code === option.code &&
                                    existingOption.name === option.name
                            ).length == 0
                        ) {
                            optionsArr.push({ code: option.code, name: option.name });
                        }
                    });
                    optionsArr?.forEach((option, i) => {
                        menuOptions.push(
                            <MenuItem value={option.code} key={i + 1}>
                                {option.name}
                            </MenuItem>
                        );
                    });
                }
                return menuOptions;
            } else if (fieldInfo.name === allFields.priority) {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                if (fieldOptions?.codedValues?.length) {
                    fieldOptions.codedValues.forEach((option, i) => {
                        menuOptions.push(
                            <MenuItem value={option.code} key={i + 1}>
                                {option.name}
                            </MenuItem>
                        );
                    });
                }
                return menuOptions;
            } else if (
                fieldInfo.name === allFields.fundingSource ||
                fieldInfo.name === allFields.contributedFundingSource
            ) {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                fundingSourceOptions?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem
                            value={option[FieldNames.fundingSources.fundingSource]}
                            key={i + 1}
                        >
                            {option[FieldNames.fundingSources.fundingSource]}
                        </MenuItem>
                    );
                });
                return menuOptions;
            } else if (fieldInfo.name === allFields.vegDeparturePercentageManual) {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                departureOptions?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option} key={i + 1}>
                            {option}
                        </MenuItem>
                    );
                });
                return menuOptions;
            } else {
                let menuOptions = [];
                menuOptions.push(<MenuItem value='' key='0' style={{ height: 30 }}></MenuItem>);
                fieldOptions?.codedValues?.forEach((option, i) => {
                    menuOptions.push(
                        <MenuItem value={option.code} key={i + 1}>
                            {option.name}
                        </MenuItem>
                    );
                });
                return menuOptions;
            }
        }
    };

    const inputValueMissing = () => {
        if (detail && detail.attributes) {
            const fieldOptions = fieldInfo.domain;
            if (!fieldIsStatic && (!detail.attributes[field] || !selectValue)) {
                if (
                    field === allFields.isSagebrush &&
                    (!detail.attributes[allFields.fundingAgency] ||
                        !detail.attributes[allFields.fundingAgency].includes(
                            FieldNames.agencies.blm
                        ))
                ) {
                    return false;
                }
                return true;
            } else {
                if (fieldInfo.name === allFields.initiationFiscalQuarter) {
                    return false;
                } else if (fieldInfo.name === allFields.estimatedSuccessProbability) {
                    const arrayForOptions = [...fieldOptions.codedValues];
                    const arrayOfNames = arrayForOptions?.map((x) => x.name);
                    const selectValueRep = selectValue?.replace(/'/g, '');
                    const fieldRep = detail.attributes[field]?.replace(/'/g, '');
                    if (
                        arrayOfNames?.length > 0 &&
                        (arrayOfNames.includes(selectValueRep) || arrayOfNames.includes(fieldRep))
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (fieldInfo.name === allFields.class) {
                    //Class is importing in as Treatment or Activity, but auto populates as Estimated Treatment or Estimated Activity
                    if (selectValue || detail.attributes[field]) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (fieldInfo.name === allFields.feasibility) {
                    const arrayForOptions = [...fieldOptions.codedValues];
                    const arrayOfNames = arrayForOptions.map((option) => option.name);
                    if (
                        arrayOfNames &&
                        (arrayOfNames.includes(selectValue) ||
                            arrayOfNames.includes(detail.attributes[field]))
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (fieldInfo.name === allFields.department) {
                    if (
                        selectValue &&
                        (selectValue.toLowerCase() !== 'n/a' ||
                            detail.attributes[
                                FieldNames.featureFields.locationDepartmentNaValid
                            ] === FieldNames.yesNo.yes)
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (fieldInfo.name === allFields.type) {
                    if(isPostFire && valueReference){
                        return (
                            valueReference.find(
                                (f) =>
                                    f[FieldNames.postFireValuesReferenceFields.type] ===
                                    detail.attributes[field]
                            ) === undefined
                        );
                    } else
                    if (fieldOptions && fieldOptions.codedValues) {
                        const arrayForOptions = [
                            ...fieldOptions.codedValues,
                            { code: selectValue, name: selectValue },
                        ];
                        const arrayOfNames = arrayForOptions.map((x) => x.name);
                        if (
                            arrayOfNames &&
                            (arrayOfNames.includes(selectValue) ||
                                arrayOfNames.includes(detail.attributes[field]))
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                } else if (fieldInfo.name === allFields.subType) {
                    if(isPostFire && valueReference){
                         return (
                             valueReference.find(
                                 (f) =>
                                     f[FieldNames.postFireValuesReferenceFields.subType] ===
                                     detail.attributes[field]
                             ) === undefined
                         );
                    } else  if (fieldOptions && fieldOptions.codedValues) {
                        const arrayForOptions = [
                            ...fieldOptions.codedValues,
                            { code: selectValue, name: selectValue },
                        ];
                        const arrayOfNames = arrayForOptions.map((x) => x.name);
                        if (
                            arrayOfNames &&
                            (arrayOfNames.includes(selectValue) ||
                                arrayOfNames.includes(detail.attributes[field]))
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                } else if (fieldInfo.name === allFields.unitOfMeasure) {
                    if (fieldOptions && fieldOptions.codedValues) {
                        const arrayForOptions = [
                            ...fieldOptions.codedValues,
                            { code: selectValue, name: selectValue },
                        ];
                        const arrayOfNames = arrayForOptions.map((x) => x.name);
                        if (
                            arrayOfNames &&
                            (arrayOfNames.includes(selectValue) ||
                                arrayOfNames.includes(detail.attributes[field]))
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                } else if (fieldInfo.name === allFields.priority) {
                    let arrayOfNames = [];
                    if (fieldOptions.codedValues && fieldOptions.codedValues.length) {
                        const arrayForOptions = [...fieldOptions.codedValues];
                        if (arrayForOptions && arrayForOptions.length) {
                            arrayOfNames = arrayForOptions.map((option) => option.name);
                        }
                    }
                    if (
                        arrayOfNames &&
                        (arrayOfNames.includes(selectValue) ||
                            arrayOfNames.includes(detail.attributes[field]))
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (
                    fieldInfo.name === allFields.fundingSource ||
                    fieldInfo.name === allFields.contributedFundingSource
                ) {
                    const arrayForOptions = [...fundingSourceOptions];
                    const arrayOfNames = arrayForOptions.map(
                        (option) => option[FieldNames.fundingSources.fundingSource]
                    );
                    if (
                        arrayOfNames &&
                        (arrayOfNames.includes(selectValue) ||
                            arrayOfNames.includes(detail.attributes[field]))
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                } else {
                    if (fieldOptions && fieldOptions.codedValues) {
                        const arrayForOptions = [...fieldOptions.codedValues];
                        const arrayOfNames = arrayForOptions.map((x) => x.name);
                        if (
                            arrayOfNames &&
                            (arrayOfNames.includes(selectValue) ||
                                arrayOfNames.includes(detail.attributes[field]))
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                }
                return false;
            }
        } else {
            return true;
        }
    };

    const showNotSavedClasses = (field) => {
        var fieldState = false;
        if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.treatment
        ) {
            // Estimated Treatment
            if (treatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = treatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualTreatment
        ) {
            // Actual Treatment
            if (actualsTreatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsTreatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity
        ) {
            // Estimated Activity
            if (activityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = activityValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualActivity
        ) {
            // Actual Activity
            if (actualsActivityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsActivityValidationUnsavedValues[field];
            return fieldState;
        } else {
            return false;
        }
    };

    /**
     * Returns whether or not this level should have the select disabled based in conditional logic.
     * Expectation is that getIsLevelDisabled will return a true or false, that detail and field will exist.
     * @param detail - The feature we are currently working with
     * @param field - The field that we are evaluating
     * @returns - True or False
     */
    const getIsLevelDisabled = (detail, field) => {
        let isDisabled = false;
        if (isPostFire) {
            if (field === allFields.category || field === allFields.type || field === allFields.subType) {
                isDisabled = !detail.attributes[FieldNames.featureFields.program];
            } else if (field === FieldNames.featureFields.program) {
                // Disable program if a line of funding exists
                // Using funding department as a quick check as it's guaranteed for any line of funding
                isDisabled = !!detail.attributes[FieldNames.featureFields.fundingDepartment];
            }
        } else {
            // Fuels
            if (
                detail?.attributes?.[FieldNames.featureFields.fundingSourceCategory]?.includes(
                    'Interagency Support'
                )
            ) {
                return (isDisabled = true);
            }
        }

        return isDisabled;
    };
    function checkPostFireCategorySelect(categoryExists, typeExists, subTypeExists) {
        if (!subTypeExists) {
            setSubTypeValue('');
        }

        if (!typeExists) {
            setTypeValue('');
        }

        if (!categoryExists) {
            setCategoryValue('');
        }
    }

    function getPostFireValues(categoryStr, typeStr, subTypeStr) {
        let currentOverrides = {
            [FieldNames.postFireValuesReferenceFields.category]: categoryStr,
            [FieldNames.postFireValuesReferenceFields.type]: typeStr,
            [FieldNames.postFireValuesReferenceFields.subType]: subTypeStr,
        };
        const categoryExists = categoryStr ?  getFilteredCategoryAssociations(
            valueReference,
            detail.attributes,
            FieldNames.categoryAssociationFields.categories,
            currentOverrides
        ).includes(categoryStr) : false;

        const typeExists = typeStr ?  getFilteredCategoryAssociations(
            valueReference,
            detail.attributes,
            FieldNames.categoryAssociationFields.types,
            currentOverrides
        ).includes(typeStr) : false

        const subTypeExists = subTypeStr ? getFilteredCategoryAssociations(
            valueReference,
            detail.attributes,
            FieldNames.categoryAssociationFields.subTypes,
            currentOverrides
        ).includes(subTypeStr) : false;

        return {
            categoryExists, typeExists, subTypeExists
        };
    }

    function handlePostFireValueChecking(categoryVal, typeVal, subTypeVal){
        const { categoryExists, typeExists, subTypeExists } = getPostFireValues(
            categoryVal,
            typeVal,
            subTypeVal
        );
        checkPostFireCategorySelect(categoryExists, typeExists, subTypeExists);
    }

    function handleEmptySelect() {
        //Empty case
        setSelectValue('');
        if (field === allFields.class) {
            setClassValue('');
            setCategoryValue('');
        } else if (field === allFields.program) {
            setProgramValue('');
            setCategoryValue('');
            setTypeValue('');
            setSubTypeValue('');
            setUnitOfMeasureValue('');
        } else if (field === allFields.category) {
            if(isPostFire) {
             handlePostFireValueChecking('', typeValue, subTypeValue);
            } else {
                setTypeValue('');
                setSubTypeValue('');
            }
            setCategoryValue('');
            setUnitOfMeasureValue('');
        } else if (field === allFields.type) {
            if (isPostFire) {
                handlePostFireValueChecking(categoryValue, '', subTypeValue);
            } else {
                setTypeValue('');
                setSubTypeValue('');
            }
                setSubTypeValue('');
            setTypeValue('');
            setUnitOfMeasureValue('');
        } else if (field === allFields.subType) {
            if (isPostFire) {
                 handlePostFireValueChecking(categoryValue, typeValue, '');
            } else {
                setSubTypeValue('');
            }
            setUnitOfMeasureValue('');
        } else if (field === allFields.unitOfMeasure) {
            setUnitOfMeasureValue('');
        }
    }

    function handleSelectNewValue(event) {
        const value = event.target.value;
        setSelectValue(value);
        if (field === allFields.class) {
            setClassValue(value);
            setCategoryValue('');
        } else if (field === allFields.program) {
            setProgramValue(value);
            setCategoryValue('');
            setTypeValue('');
            setSubTypeValue('');
            setUnitOfMeasureValue('');
        } else if (field === allFields.category) {
            if(isPostFire) {
                 handlePostFireValueChecking(value, typeValue, subTypeValue);
            }
            setCategoryValue(value);
            setUnitOfMeasureValue('');
        } else if (field === allFields.type) {
            setTypeValue(value);
            if(isPostFire) {
                 handlePostFireValueChecking(categoryValue, value, subTypeValue);
            } else {
                setSubTypeValue('');
            }
            setUnitOfMeasureValue('');
            setUnitOfMeasureValue('');
        } else if (field === allFields.subType) {
            setSubTypeValue(value);
            if(isPostFire) {
                 handlePostFireValueChecking(categoryValue, typeValue, value);
            }
            setUnitOfMeasureValue('');
        } else if (field === allFields.unitOfMeasure) {
            setUnitOfMeasureValue(value);
        }
    }

    useEffect(() => {
        let displayText = '';
        if (field === FieldNames.featureFields.program) {
            // Display tooltip if disabled
            displayText = detail?.attributes?.[FieldNames.featureFields.fundingDepartment]
                ? true
                : false;
            if (displayText && !tooltipText) {
                setTooltipText(
                    'You must delete your funding lines prior to changing the program value for this action.'
                );
            }
        }
        if (!displayText && tooltipText) {
            setTooltipText('');
        }
    }, [field, tooltipText, detail?.attributes?.[FieldNames.featureFields.fundingDepartment]]);

    return (
        <FormControl variant='standard' fullWidth>
            <ThemeProvider theme={theme}>
                <Tooltip title={tooltipText} placement='top'>
                    <Select
                        key={`${field}`}
                        sx={isActual ? { width: 240 } : { minWidth: 240 }}
                        renderValue={(value) => `${value}`}
                        defaultValue=''
                        {...(isRequired && inputValueMissing()
                            ? { className: classes.underline }
                            : showNotSavedClasses(field)
                              ? { className: notSavedClasses.underline }
                              : { className: '' })}
                        value={selectValue}
                        onChange={(event) => {
                            // check value type, check if it exists in the subtypes, and go from there
                            // if category, get type subtype value, see if they match, if not reset
                            if (event.target.value === '') {
                                handleEmptySelect();
                            } else {
                                handleSelectNewValue(event);
                            }
                            onFieldChange(field, event.target.value);
                        }}
                        autoWidth
                        disabled={
                            fieldIsStatic ||
                            getAllApprovalFieldsCount(detail, isRequired) ||
                            getIsLevelDisabled(detail, field) ||
                            fieldIsDisabled
                        }
                    >
                        {getItemsForDomain(fieldInfo)}
                    </Select>
                </Tooltip>
                {detail &&
                    detail.attributes &&
                    detail.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes &&
                    (field === allFields.type || field === allFields.category) &&
                    detail.attributes[FieldNames.featureFields.class] ===
                        FieldNames.classTypes.activity && (
                        <FormHelperText>
                            Options are reduced due to the Geometry being a Point
                        </FormHelperText>
                    )}
            </ThemeProvider>
        </FormControl>
    );
}
